import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, CheckCircle, Building, MapPin, Calendar, DollarSign, FileText, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const TimeshareInfoForm = () => {
  const { clientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    resort_name: '',
    resort_location: '',
    ownership_type: '',
    week_number: '',
    unit_number: '',
    points_amount: '',
    contract_number: '',
    owners_on_contract: '',
    maintenance_fee: '',
    loan_balance: '',
    mortgage_company: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.resort_name || !formData.resort_location || !formData.ownership_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/public/timeshare-info/${clientId}`, formData);
      setSubmitted(true);
      toast.success('Information submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit information');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Thank You!</h2>
            <p className="text-slate-600">
              Your timeshare information has been submitted successfully. Our team will review your information and contact you shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Timeshare Information Request</h1>
          <p className="text-slate-600">
            Please provide your timeshare details to help us prepare your proposal.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building className="h-5 w-5 text-accent" />
              Timeshare Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Resort Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <Building className="h-3.5 w-3.5" />
                    Resort Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.resort_name}
                    onChange={(e) => handleChange('resort_name', e.target.value)}
                    placeholder="Enter resort name"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    Resort Location <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.resort_location}
                    onChange={(e) => handleChange('resort_location', e.target.value)}
                    placeholder="City, State/Country"
                    className="mt-1"
                    required
                  />
                </div>
              </div>

              {/* Ownership Type */}
              <div>
                <Label className="flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" />
                  Ownership Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.ownership_type}
                  onValueChange={(value) => handleChange('ownership_type', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select ownership type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Week-Based</SelectItem>
                    <SelectItem value="points">Points-Based</SelectItem>
                    <SelectItem value="hybrid">Hybrid (Week + Points)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Week/Points Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Week Number
                  </Label>
                  <Input
                    value={formData.week_number}
                    onChange={(e) => handleChange('week_number', e.target.value)}
                    placeholder="e.g., 25"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Unit Number</Label>
                  <Input
                    value={formData.unit_number}
                    onChange={(e) => handleChange('unit_number', e.target.value)}
                    placeholder="e.g., 2BR-505"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Points Amount</Label>
                  <Input
                    type="number"
                    value={formData.points_amount}
                    onChange={(e) => handleChange('points_amount', e.target.value)}
                    placeholder="e.g., 150000"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Contract Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contract Number</Label>
                  <Input
                    value={formData.contract_number}
                    onChange={(e) => handleChange('contract_number', e.target.value)}
                    placeholder="Enter contract number"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    Owners on Contract
                  </Label>
                  <Input
                    value={formData.owners_on_contract}
                    onChange={(e) => handleChange('owners_on_contract', e.target.value)}
                    placeholder="e.g., John Doe, Jane Doe"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Financial Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <DollarSign className="h-3.5 w-3.5" />
                    Annual Maintenance Fee
                  </Label>
                  <Input
                    type="number"
                    value={formData.maintenance_fee}
                    onChange={(e) => handleChange('maintenance_fee', e.target.value)}
                    placeholder="$0.00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <DollarSign className="h-3.5 w-3.5" />
                    Loan Balance
                  </Label>
                  <Input
                    type="number"
                    value={formData.loan_balance}
                    onChange={(e) => handleChange('loan_balance', e.target.value)}
                    placeholder="$0.00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Mortgage Company</Label>
                  <Input
                    value={formData.mortgage_company}
                    onChange={(e) => handleChange('mortgage_company', e.target.value)}
                    placeholder="Enter mortgage company"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full bg-accent hover:bg-accent/90"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Submit Information
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Your information is secure and will only be used to process your request.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TimeshareInfoForm;
